import { AlertCircle, Loader2, ShieldOff } from 'lucide-react';

export function BillingGate({ canBill, children }: { canBill: boolean | null; children: React.ReactNode }) {
  if (canBill === null) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 text-primary-600 animate-spin" />
      </div>
    );
  }
  if (!canBill) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center">
        <ShieldOff className="h-10 w-10 text-neutral-400 mx-auto mb-3" />
        <h2 className="text-lg font-semibold text-neutral-900">Billing access required</h2>
        <p className="text-sm text-neutral-600 mt-1">Ask a platform administrator to add you as a billing manager.</p>
      </div>
    );
  }
  return <>{children}</>;
}

export function ErrorBanner({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div className="mb-4 bg-error-50 border border-error-200 rounded-lg p-3 text-sm text-error-700 flex items-start gap-2">
      <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
      <span className="min-w-0 break-words">{message}</span>
    </div>
  );
}

export function Field({ label, htmlFor, hint, children }: { label: string; htmlFor: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <label htmlFor={htmlFor} className="block text-sm font-medium text-neutral-700 mb-1">{label}</label>
      {children}
      {hint && <p className="text-xs text-neutral-500 mt-1">{hint}</p>}
    </div>
  );
}

/** Customer-facing wording of a raw invoice status, plus the ones only billing sees. */
export function InvoiceStatusBadge({ status, dueDate }: { status: string | null; dueDate?: string | null }) {
  const todayStr = new Date().toISOString().slice(0, 10);
  const overdue = (status === 'AUTHORISED' || status === 'SUBMITTED') && !!dueDate && dueDate < todayStr;
  const map: Record<string, [string, string]> = {
    PAID: ['Paid', 'bg-green-100 text-green-800'],
    AUTHORISED: ['Awaiting payment', 'bg-yellow-100 text-yellow-800'],
    SUBMITTED: ['Awaiting payment', 'bg-yellow-100 text-yellow-800'],
    DRAFT: ['Draft', 'bg-neutral-100 text-neutral-700'],
    VOIDED: ['Void', 'bg-neutral-100 text-neutral-500'],
    FAILED: ['Invoice failed', 'bg-red-100 text-red-800'],
    NOT_CONNECTED: ['Not sent — Xero offline', 'bg-orange-100 text-orange-800'],
  };
  const [label, cls] = overdue ? ['Overdue', 'bg-red-100 text-red-800'] : (map[status ?? ''] ?? ['No invoice', 'bg-neutral-100 text-neutral-500']);
  return <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap ${cls}`}>{label}</span>;
}
